import {
  ColDef,
  ColumnGroup,
  CsvExportParams,
  EditableCallback,
  ExcelExportParams,
  GetMainMenuItemsParams,
  ICellRendererParams,
  MenuItemDef,
  PasteStartEvent,
  ValueFormatterFunc,
  ValueFormatterParams,
  ValueGetterFunc,
  ValueGetterParams,
  ValueParserFunc,
  ValueParserParams,
  ModelUpdatedEvent,
  EditableCallbackParams,
  CellValueChangedEvent,
  PostProcessPopupParams,
} from 'ag-grid-community';
import { RowGroupingDisplayType } from 'ag-grid-enterprise';
import { ElementRef, Type } from '@angular/core';
import { Observable } from 'rxjs';
import { FilterType, HasEDPIngestionMethod } from '@miq/catalog';
// TODO: remove "reports & records" refs out from share module, (modules should not have bidirectional dependencies)
import { AddressHeaderRendererComponent } from '@reports/report-details/corporate/mimo-report/address-header-renderer/address-header-renderer.component';
import { LastGroupRendererComponent } from '@reports/report-details/corporate/multi-period/last-group-renderer/last-group-renderer.component';
import { Validation, Validator } from '@shared/models/validations/validators.model';
import { ColumnModel } from '@shared/models/columns/column-catalog.model';
import { Data } from '@shared/models/data-object.model';
import { IOption } from '@shared/models/icommons.model';
import { AgTableCellRendererComponent } from './ag-table-cell-renderer.component';
import { GoToRecordRendererComponent } from './go-to-record-renderer.component';
import { IconCellRendererComponent } from './icon-cell-renderer.component';
import { AgDateComponent } from './inputs/ag-date.component';
import { AgNaicsInputComponent } from './inputs/ag-naics-input.component';
import { AgNumericComponent } from './inputs/ag-numeric.component';
import { AgPopupTextComponent } from './inputs/ag-popup-text.component';
import { AgSelectComponent } from './inputs/ag-select.component';
import { AgUomComponent } from './inputs/ag-uom.component';
import { HasChangeCommandHandler } from './change-event-command';
import { AgPropertySetCellRendererComponent } from './property-set-cell-renderer/ag-property-set-cell-renderer.component';
import { ImagePreviewCellRendererComponent } from './image-preview-cell-renderer/image-preview-cell-renderer.component';
import { TimePeriodValidationCellRendererComponent } from './time-period-validation-renderer.component';
import { MiqCostarLinkRendererComponent } from '@shared/components/table/costar-link-renderer.component';
import { AgTagEditorComponent } from './inputs/ag-tag-editor.component';

export interface CanValidate<T = Record<string, any>> {
  formControlOptions?: { validatorFn?: Validation<T> };
  validatorPlugin?: Type<Validator<T>>;
  warningValidatorPlugin?: Type<Validator<T>>;
}

export type AgCallback = (...x: any[]) => any;

export enum grandTotalRow {
  Top = 'top',
  Bottom = 'bottom',
  PinnedTop = 'pinnedTop',
  PinnedBottom = 'pinnedBottom',
}

export enum RowGroupingDisplay {
  SingleColumn = 'singleColumn',
  MultipleColumns = 'multipleColumns',
  GroupRows = 'groupRows',
  Custom = 'custom',
}

export interface AgGridOptions {
  style: any;
  rowClass: string;
  rowClassRules: any;
  singleClickEdit: boolean;
  editType: AgGridOptionsEditType;
  suppressClickEdit: boolean;
  suppressMovableColumns?: boolean;
  // Suppress Keyboard Event and Suppress Cell Selection must be false inorder to be able to paste
  suppressKeyboardEvent: any;
  suppressCellFocus: boolean;
  domLayout: AgGridOptionsDomLayout;
  autoSizeAll: boolean;
  pagination: boolean;
  paginationPageSize: number | string;
  tooltipShowDelay: number;
  context: any;
  suppressAggFuncInHeader: boolean;
  groupSuppressAutoColumn: boolean;
  getContextMenuItems: AgCallback;
  getRowNodeId: AgCallback;
  icons: any;
  grandTotalRow: 'top' | 'bottom' | 'pinnedTop' | 'pinnedBottom';
  processCellForClipboard: AgCallback;
  processDataFromClipboard: AgCallback;
  groupDefaultExpanded: number;
  rowDragManaged: boolean;
  rowHeight: number;
  stopEditingWhenGridLosesFocus: boolean;
  autoGroupColumnDef: { [key: string]: any };
  excelStyles: Array<{ [key: string]: any }>;
  noRowsToShow?: string;
  undoRedoCellEditing?: boolean;
  suppressColumnVirtualisation?: boolean;
  suppressContextMenu?: boolean;
  rowModelType?: AgRowModelType;
  dataStream?: (input: AgStreamParams) => Observable<AgStreamResult>;
  suppressPaginationPanel?: boolean;
  paginationPageSizeOptions?: Array<number | IOption>;
  treeData?: boolean;
  checkboxSelection?: boolean;
  rowSelection?: any;
  cellSelection?: any;
  getDataPath?: AgCallback;
  defaultGroupSortComparator?: AgCallback;
  groupHideOpenParents?: boolean;
  groupDisplayType?: RowGroupingDisplayType;
  defaultCsvExportParams?: CsvExportParams;
  defaultExcelExportParams?: ExcelExportParams;
  unSortIcon?: boolean;
  suppressMultiSort?: boolean;
  sortingOrder?: ('asc' | 'desc' | null)[];
  multiSortKey?: string;
  animateRows?: boolean;
  suppressDragLeaveHidesColumns: boolean;
  suppressColumnMenu?: boolean;
  getMainMenuItems?: (params: GetMainMenuItemsParams) => (string | MenuItemDef)[];
  onPasteStart?: (event: PasteStartEvent) => void;
  onPasteEnd?: (event: PasteStartEvent) => void;
  groupAllowUnbalanced?: boolean;
  paginationPageSizeSelector?: boolean;
  suppressRowVirtualisation?: boolean;
  suppressAnimationFrame?: boolean;
  suppressColumnMoveAnimation?: boolean;
  suppressClipboardPaste?: boolean | ((params: PasteStartEvent) => boolean);
  onModelUpdated?: (event: ModelUpdatedEvent) => void;
  popupParent?: HTMLElement;
  postProcessPopup?: (params: PostProcessPopupParams) => void;
}

export interface AgStreamParams {
  startRow: number;
  endRow: number;
  sort: AgSortParams[];
  filters: { [key: string]: AgFilterParams };
}

export enum AgFilterType {
  Number = 'number',
  Text = 'text',
  Date = 'date',
}

export interface AgFilterParams {
  // values obtained from number filters
  filter: number | string;
  filterTo: number;
  /* the date value(s) associated with the filter.
   the type is string and format is always YYYY-MM-DD e.g. 2019-05-24
   custom filters can have no values (hence both are optional).
   range filter has two values (from and to). */
  dateFrom?: string;
  dateTo?: string;
  filterType: string;
  type: string;
  values: string[];
}

export interface AgSortParams {
  colId: string;
  sort: 'asc' | 'desc';
}

export interface AgStreamResult {
  data: any[];
  totalRows: number;
}

export const enum AgGridOptionsEditType {
  CompleteTable = 'completeTable',
  FullRow = 'fullRow',
  OneAtATime = '',
}

export const enum AgGridOptionsDomLayout {
  AutoHeight = 'autoHeight',
  Normal = 'normal',
}

export const enum AgRowModelType {
  ClientSide = 'clientSide',
  Infinite = 'infinite',
  ViewPort = 'viewport',
  ServerSide = 'serverSide',
}

export const enum AgGridPinnedType {
  Left = 'left',
  Right = 'right',
}

export enum AgGridFilterOptons {
  InRange = 'inRange',
  GreaterThan = 'greaterThan',
  LessThan = 'lessThan',
}

export const MiqAgCellRenderers = {
  miqAgCellRenderer: AgTableCellRendererComponent,
  miqIconCellRenderer: IconCellRendererComponent,
  miqGoesToRecordRenderer: GoToRecordRendererComponent,
  miqLastGroupRenderer: LastGroupRendererComponent,
  miqAddressHeaderRenderer: AddressHeaderRendererComponent,
  miqPropertySerCellRenderer: AgPropertySetCellRendererComponent,
  miqImagePreviewCellRenderer: ImagePreviewCellRendererComponent,
  timePeriodWithValidationRenderer: TimePeriodValidationCellRendererComponent,
  miqCostarLinkRenderer: MiqCostarLinkRendererComponent,
};

/**
 * CellEditor component name - type mapping. To chose what component
 * to render as cell editor on ag-grid tables
 */
export const MiqAgCellEditors = {
  agNumberComponent: AgNumericComponent,
  agSelectComponent: AgSelectComponent,
  agDateComponent: AgDateComponent,
  agPopupTextComponent: AgPopupTextComponent,
  agUomComponent: AgUomComponent,
  agNaicsInputComponent: AgNaicsInputComponent,
  agTagComponent: AgTagEditorComponent,
};

export interface CellRenderParams extends Data {
  /**
   * Used when cell template is ```miqIconCellRenderer```
   * You can specify either a string svg icon name or a function that returns the icon name
   */
  icon: string | ((data: ICellRendererParams) => string);
  /**
   * Used when cell template is ```miqIconCellRenderer```
   * You can specify the callback function when the icon is clicked
   */
  onIconClick: (data: ICellRendererParams, $event, elementRef?: ElementRef) => void;
}

export interface CellEditorParams extends Data {
  /**
   * Used when cell editor is ```agTextComponent```
   * The title will be displayed  in the popup
   */
  title?: string;
  /**
   * Used when cell editor is ```agTextComponent```
   * If true, the text cannot be modified
   */
  isReadOnly: (params: ICellRendererParams) => boolean;

  /**
   * Used when cell editor is ```agNumberComponent```
   * Allow the input number to be negative
   */
  allowNegativeNumbers: boolean;

  /**
   * Used when cell editor is ```agNumberComponent```
   * Restricts the input to be a decimal value
   */
  isIntegerValue?: boolean;
}

export const enum DisplayFormat {
  Uom = 'uom',
  Complex = 'complex',
  Currency = 'currency',
  Quarter = 'quarter',
  Date = 'date',
  DateWithTimestamp = 'date with timestamp',
  Numeric = 'number',
}

interface DataExtraction {
  field: string;
  condition: any;
  dataType: string;
  inputType: string;
  rawData: boolean;
}

export interface DisplayFnSettings {
  value: any;
  appendFieldPath: string;
  fieldPath: string;
  node: any;
  displayFormat: DisplayFormat;
  dataExtract: DataExtraction;
  valuePath: string;
  mappingLOV?: IOption[];
}

export const setTranslationsForAGGrid = (columnInfo: any, localeData: any) => {
  columnInfo.forEach(element => {
    element.headerName = localeData[element.headerName] || element.headerName;
    if (element.children) {
      element.children.forEach(child => {
        child.headerName = localeData[child.headerName] || child.headerName;
      });
    }
  });
  return columnInfo;
};

/**
 * Generic utility to exclude specific set of attributes from T
 * DEVELOPER NOTE: this is not needed for typescript version above 3.5
 * since we are still in 3.1 we need to provide this alternative
 * once we upgrade to at least 3.5 we can remove this type
 */
type Omit<T, K extends keyof T> = Pick<T, Exclude<keyof T, K>>;

/**
 * Miq version for ValueFormatter Params type (include Miq's colDef)
 */
export type MiqValueFormatterParams = ValueFormatterParams & { colDef: MiqAgColumn };
/**
 * Miq version for ValueFormatter Func type (include Miq's colDef)
 */
export interface MiqValueFormatterFunc<TData = any, TValue = any> extends ValueFormatterFunc<TData, TValue> {
  (params: MiqValueFormatterParams): string;
}

/**
 * Miq version for ValueGetter Params type (include Miq's colDef)
 */
export type MiqValueGetterParams<TData = any, TValue = any> = ValueGetterParams<TData, TValue> & {
  colDef: MiqAgColumn;
};
/**
 * Miq version for ValueGetter Function type  (include Miq's colDef)
 */
export interface MiqValueGetterFunc<TData = any, TValue = any> extends ValueGetterFunc<TData, TValue> {
  (params: MiqValueGetterParams): any;
}

/**
 * Miq version for ValueParser Params type (include Miq's colDef)
 */
export type MiqValueParserParams<TData = any, TValue = any> = ValueParserParams<TData, TValue> & {
  colDef: MiqAgColumn;
};
/**
 * Miq version for ValueParser Function type (include Miq's colDef)
 */
export interface MiqValueParserFunc extends ValueParserFunc {
  (params: MiqValueParserParams): any;
}

export interface ValueForTracking {
  event: CellValueChangedEvent;
  nextValue: any;
  field: string;
  initialRowData: Record<string, any> | any;
}

export interface ShowUndoParams {
  params: EditableCallbackParams | any;
  originalValue: any;
}

export interface MiqAgColumn<TData = any, Tvalue = any>
  extends
    ColDef<TData, Tvalue>,
    // we need to exclude displayFn & valueSetter because signature for args are different,
    // and also pinned because is not only a boolean for AgGrid columns
    Partial<
      Omit<
        ColumnModel,
        | 'columnId'
        | 'field'
        | 'displayFn'
        | 'valueSetter'
        | 'pinned'
        | 'header'
        | 'type'
        | 'filter'
        | 'unit'
        | 'validatorPlugin'
        | 'warningValidatorPlugin'
        | 'formControlOptions'
      >
    >,
    Partial<HasEDPIngestionMethod>,
    CanValidate<any>,
    HasChangeCommandHandler,
    Data {
  /**
   * Customized Value formatter Func
   */
  valueFormatter?: string | MiqValueFormatterFunc<any, any>;

  /**
   * Customized Value getter Func
   */
  valueGetter?: string | MiqValueGetterFunc;

  /**
   * Customized Value parser Func
   */
  valueParser?: string | MiqValueParserFunc;

  /**
   * Display fn to be used when dataCellFormatter or BAU process to render cell values is required
   * Ideally we should use valueFormatter instead which is the equivalent version
   */
  displayFn?: <T = any>(data: T, settings?: DisplayFnSettings, ...args: any[]) => string;
  /**
   * When using displayFn, this property will be sent as param of the display function
   */
  settings?: any;
  /**
   * Column.filter version for AgColumn since AGColumn does not share the same
   */
  canFilter?: boolean;
  /**
   * Column.type (used in backend to create ES queries) on AgColumn version
   */
  filterType?: string | FilterType;

  /**
   * Grouped columns configuration
   */
  children?: MiqAgColumn[];
  /**
   * TODO: move somewhere that makes sense
   */
  isIntegerValue?: boolean;
  /**
   * self reference, useful when interface is "composed" on the fly  like `MiqAgColumn & { thisOtherField:any }`, so it doesn't fail
   */
  unit?: this;
  /**
   * configure custom comparator for sorting
   */
  comparator?: (valueA: any, valueB: any, nodeA: any, nodeB: any, isDescending: boolean) => number;
  editable?:
    | boolean
    | EditableCallback<any, any>
    | ((params: EditableCallbackParams<any, any>, additionalParams?: any) => boolean);
  /**
   * Set the header class list to be used for the column
   */
  headerClass?: string[] | any;

  /**
   * Get the value for tracking the original state of a cell
   * @param event The cell value changed event
   * @param nextValue The new value of the cell
   * @param field The field name of the cell
   * @param initialRowData The initial row data before any changes
   * @returns The value to be used for tracking the original state
   */
  getValueForTrackingOriginal?: (params: ValueForTracking) => any;

  /**
   * Determine if the undo button should be shown for a cell
   * @param params The editable callback parameters
   * @param originalValue The original value of the cell
   * @returns A boolean indicating whether the undo button should be shown
   */
  showUndo?: (params: ShowUndoParams) => boolean;

  /**
   * Flag to indicate that after pasting, the post calculation should be applied to update the cell value based on the pasted value
   */
  applyPostPopUpCalculation?: boolean;
}

export interface MiqColumnGroup extends ColumnGroup {
  /**
   * Set the header class list to be used for the column
   */
  headerClass?: string[];
}
